/**
 * PortfoliYo Lab — auth context (PB-4; replaces the IL-8.3 version).
 *
 * **What changed, and why the file was rewritten rather than edited.** The IL-8.3 context was a
 * near-copy of Trade's `AuthContext` with one deliberate hole: no logout, because logging out meant
 * deleting a row from Trade-owned `sessions`, so the shell linked out to Trade for it — and, by the
 * same logic, for signing *in*. Lab could show you who you were and nothing else.
 *
 * PB-2/PB-3 gave Lab `lab_users` and `lab_sessions`, so every one of those constraints is gone. This
 * context owns the whole lifecycle: request a code, redeem it, hold the session, end it. There is no
 * `VITE_TRADE_ORIGIN` in here at all — Lab does not need Trade to be running, or even to exist, to
 * log somebody in. That is the milestone, expressed in the one file that used to contradict it.
 *
 * `loading` is rendered explicitly by every consumer rather than collapsing into "signed out":
 * showing a login screen for a moment to someone who is already signed in reads as a session having
 * been lost.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { labAuthApi, type LabUser } from './labAuthApi';

interface LabAuthContextType {
  user: LabUser | null;
  /** True until the first session check resolves. Distinct from "signed out". */
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Send a login code. Throws `LabAuthError` — 429 carries `retryAfterSeconds`. */
  requestCode: (email: string) => Promise<void>;
  /** Redeem a code. On success the session cookie is set and `user` is populated. */
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const LabAuthContext = createContext<LabAuthContextType | undefined>(undefined);

export function LabAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<LabUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const status = await labAuthApi.getSession();
      setUser(status.authenticated ? status.user : null);
    } catch (error) {
      // The endpoint answers 200 with `authenticated: false` when signed out, so reaching here
      // means it is genuinely unreachable or broken — not that nobody is signed in. Treated as
      // signed out anyway, because there is nothing useful the shell can render either way.
      console.error('[Lab] session check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const requestCode = useCallback(async (email: string) => {
    await labAuthApi.requestCode(email);
  }, []);

  const verifyCode = useCallback(async (email: string, code: string) => {
    // The response *is* the user, so there is no follow-up round trip to `/auth/me` here. One
    // fewer request, and no window in which the app is authenticated but does not know it.
    setUser(await labAuthApi.verifyCode(email, code));
  }, []);

  const logout = useCallback(async () => {
    try {
      await labAuthApi.logout();
    } finally {
      // Cleared even if the request failed. The alternative — staying "signed in" locally after
      // someone clicked sign out — is the worse of the two wrong states.
      setUser(null);
    }
  }, []);

  const value: LabAuthContextType = {
    user,
    loading,
    isAuthenticated: user !== null,
    isAdmin: user?.role === 'ADMIN',
    requestCode,
    verifyCode,
    logout,
    refresh,
  };

  return <LabAuthContext.Provider value={value}>{children}</LabAuthContext.Provider>;
}

export function useLabAuth() {
  const context = useContext(LabAuthContext);
  if (context === undefined) {
    throw new Error('useLabAuth must be used within a LabAuthProvider');
  }
  return context;
}
